import { X } from 'lucide-react'
import type { AnnouncementSettings } from '@/api/types'
import { cn } from '@/lib/utils'

interface SiteAnnouncementBarProps {
  announcement: AnnouncementSettings
  onDismiss?: () => void
  preview?: boolean
}

const LEVEL_LABELS: Record<AnnouncementSettings['level'], string> = {
  info: 'Info',
  warning: 'Warning',
  critical: 'Critical',
  pay_attention: 'Pay Attention',
}

function toneClasses(announcement: AnnouncementSettings) {
  switch (announcement.level) {
    case 'info':
      return {
        shell: 'border-sky-300/40 bg-sky-500/12 text-sky-50',
        pill: 'bg-sky-950/40 text-sky-100 ring-1 ring-sky-200/25',
      }
    case 'warning':
      return {
        shell: 'border-amber-300/40 bg-amber-500/15 text-amber-50',
        pill: 'bg-amber-950/40 text-amber-100 ring-1 ring-amber-200/25',
      }
    case 'critical':
      return {
        shell: 'border-red-300/40 bg-red-600/20 text-red-50',
        pill: 'bg-red-950/50 text-red-100 ring-1 ring-red-200/25',
      }
    case 'pay_attention':
      return {
        shell: 'border-emerald-200/40 bg-[#0d4f36] text-white',
        pill: 'bg-black/25 text-white ring-1 ring-white/20',
      }
  }
}

function animatedBackground(announcement: AnnouncementSettings) {
  if (announcement.level !== 'pay_attention') return null

  // Keep legacy "striped" values rendering with the same modern style.

  return (
    <div
      aria-hidden="true"
      className="absolute inset-0"
      style={{
        backgroundImage: 'repeating-linear-gradient(90deg, #0b3e2b 0 80px, #146646 80px 160px, #2f8f66 160px 240px)',
        backgroundSize: '240px 100%',
        animation: 'site-announcement-win98 4s linear infinite',
      }}
    />
  )
}

export function SiteAnnouncementBar({ announcement, onDismiss, preview = false }: SiteAnnouncementBarProps) {
  const tone = toneClasses(announcement)

  return (
    <div
      className={cn('relative w-full overflow-hidden border-b shadow-sm', tone.shell, preview && 'rounded-lg border')}
      role="region"
      aria-label="Site announcement"
    >
      <style>{`
        @keyframes site-announcement-win98 {
          from { background-position: 0 0; }
          to { background-position: 240px 0; }
        }
      `}</style>
      {animatedBackground(announcement)}
      <div className="absolute inset-0 bg-gradient-to-b from-white/8 to-transparent" aria-hidden="true" />
      <div className="relative z-10 flex min-h-12 items-center gap-3 px-4 py-3 lg:px-6">
        <span className={cn('inline-flex shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.18em]', tone.pill)}>
          {LEVEL_LABELS[announcement.level]}
        </span>
        <p className="flex-1 text-sm font-medium leading-5">
          {announcement.message}
        </p>
        {announcement.dismissible && onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="rounded-md p-1.5 text-current/80 transition-colors hover:bg-black/10 hover:text-current"
            aria-label="Dismiss announcement"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  )
}